/**
 * What a URL resolves to, as a decision separate from rendering it.
 *
 * This used to be a run of `if`s inside App, which was fine when signing out
 * meant "you see the login form" and nothing else. Now that the coverage map is
 * public and the form is a destination rather than a wall, the ordering carries
 * real product meaning — a visitor must be able to reach the map without an
 * account — and that is worth being able to test without a browser.
 *
 * Document routes (legal, guides) are resolved by the caller before this runs;
 * they are public regardless of session state.
 */

import { landingSlug } from "./landing";

export type Route =
  | { kind: "loading" }
  /** The public coverage map. */
  | { kind: "coverage" }
  /**
   * A county's landing page — where an ad for that county sends people.
   * Public, like the map, and for the same reason. `slug` is "" for the
   * statewide page; a slug that names no county is the caller's to resolve.
   */
  | { kind: "landing"; slug: string }
  /** The sign-in / sign-up form. */
  | { kind: "gate" }
  /** "Email me a reset link." Public, and reachable mid-session. */
  | { kind: "forgot" }
  /** The form a reset link lands on. The token is in the query string. */
  | { kind: "reset" }
  | { kind: "redirect"; to: string }
  /** Signed in, but on a superseded version of the agreement. */
  | { kind: "agreement" }
  /** A County plan with no county chosen: the data routes cannot answer. */
  | { kind: "choose-county" }
  /** Saved alerts. Gated exactly like the dashboard: it reads the same data. */
  | { kind: "alerts" }
  /** The report basket. Gated like the dashboard, and for the same reason. */
  | { kind: "reports" }
  /** The operator console. Only the admin role resolves to this. */
  | { kind: "admin" }
  | { kind: "dashboard" };

export const SIGNIN_PATH = "/signin";
export const COVERAGE_PATH = "/coverage";
//: Asking for a reset link, and the form the link lands on. Both are public —
//: see resolveRoute for why they are decided before the session is.
export const FORGOT_PATH = "/forgot";
export const RESET_PATH = "/reset";
//: The data itself. Reached by picking a county off the map, or directly.
export const DASHBOARD_PATH = "/dashboard";
//: Saved alerts. Not a public page — it lists a subscriber's own searches.
export const ALERTS_PATH = "/alerts";
//: The report basket. Not public: the price depends on the credits the account
//: holds, and the counts it prices come out of the gated tables.
export const REPORTS_PATH = "/reports";
//: The operator console. Reachable only by the admin role, and the server
//: refuses every endpoint behind it to everyone else — this constant decides
//: what is *drawn*, never what is permitted.
export const ADMIN_PATH = "/admin";

export interface RouteInput {
  pathname: string;
  /** The `county` query parameter, "" when absent. */
  county: string;
  /** False until the stored token has been checked against the server. */
  ready: boolean;
  signedIn: boolean;
  needsAcceptance: boolean;
  /** True when the plan holds one county and none has been picked yet. */
  mustChooseCounty?: boolean;
  /**
   * Whether the signed-in account holds the admin role.
   *
   * Presentation only. `require_admin` on the backend router is what makes the
   * console admin-only; this stops a link and a blank page being offered to
   * someone who would only be refused. Optional, so the *absent* case has to be
   * the safe one — a caller that forgets it must fail closed.
   */
  isAdmin?: boolean;
  /**
   * True when the server refused the token rather than the user signing out —
   * an expiry, or a sign-in on another device.
   */
  sessionEnded?: boolean;
}

export function resolveRoute(input: RouteInput): Route {
  const { pathname, county, ready, signedIn, needsAcceptance } = input;
  const mustChooseCounty = Boolean(input.mustChooseCounty);
  const sessionEnded = Boolean(input.sessionEnded);
  const isAdmin = Boolean(input.isAdmin);

  // The map answers "do you cover my county?", which nobody should have to
  // sign in — or even wait for a session check — to ask.
  if (pathname === COVERAGE_PATH) return { kind: "coverage" };

  // The county landing pages: same rule as the map. An ad click lands here,
  // and a session check it has to wait for is a flash of "Loading" on the
  // one page whose first second is paid for.
  const slug = landingSlug(pathname);
  if (slug !== null) return { kind: "landing", slug };

  // The two password-reset pages, decided before the session is and regardless
  // of what it says. Three cases would otherwise break, and all three are the
  // normal way this feature gets used:
  //
  //  * someone locked out has no session to wait for, and a "Loading" flash on
  //    a page that needs nothing from one is a page that looks broken;
  //  * a stale token in localStorage must not bounce a reset link to the
  //    dashboard — the person clicking it is precisely the person whose
  //    session is not to be trusted;
  //  * a link opened while signed in on this device still has to work, because
  //    the reason to reset is usually a session somewhere else.
  if (pathname === FORGOT_PATH) return { kind: "forgot" };
  if (pathname === RESET_PATH) return { kind: "reset" };

  if (!ready) return { kind: "loading" };

  if (!signedIn) {
    // A session that ended on its own gets the form wherever they were
    // standing. Someone working in the dashboard when their token was refused
    // would otherwise be dropped onto the public map — the same page a first
    // time visitor sees, with nothing to say why the data went away. Being
    // silently demoted to a stranger is how "it logged me out" becomes "it
    // broke".
    if (sessionEnded) return { kind: "gate" };
    // Otherwise: the form when they asked for something specific — a county
    // deep link, or Sign in itself. The map is the front door for everyone else.
    if (pathname === SIGNIN_PATH || county) return { kind: "gate" };
    return { kind: "coverage" };
  }

  // Signed in, these two have nothing left to say, and both carry the county
  // across so that whoever asked for one lands on it rather than on a
  // county-wide list they did not ask for.
  if (pathname === SIGNIN_PATH || (pathname === "/" && county)) {
    return {
      kind: "redirect",
      to: county ? `${DASHBOARD_PATH}?county=${county}` : DASHBOARD_PATH,
    };
  }

  // The console, before the two data gates below. An administrator is not a
  // subscriber: they have no county to choose, and holding them at a "choose
  // your county" screen would lock them out of the page that fixes it. The
  // agreement is not asked of them here either — it gates *data*, and nothing
  // on this page is county data.
  if (pathname === ADMIN_PATH) {
    // Not a 404 and not an error: someone who is not an admin has no business
    // knowing whether this page exists, and the map is where everything else
    // unrecognised goes.
    return isAdmin ? { kind: "admin" } : { kind: "coverage" };
  }

  if (
    pathname === DASHBOARD_PATH ||
    pathname === ALERTS_PATH ||
    pathname === REPORTS_PATH
  ) {
    // Acceptance first: it gates the data, and choosing a county is a
    // decision about data they cannot see yet.
    if (needsAcceptance) return { kind: "agreement" };
    if (mustChooseCounty) return { kind: "choose-county" };
    // Alerts are answered from the same gated tables as the dashboard, so
    // they clear the same two gates before either is rendered. Putting the
    // path here rather than in a branch of its own is what stops the next
    // page from quietly skipping them.
    // The basket clears the same two gates for the same reason the alerts
    // page does: it prices the gated tables, and buying data under a
    // superseded agreement is exactly what acceptance is for. Listing the
    // paths together rather than branching separately is what stops the next
    // page from quietly skipping them.
    if (pathname === ALERTS_PATH) return { kind: "alerts" };
    if (pathname === REPORTS_PATH) return { kind: "reports" };
    return { kind: "dashboard" };
  }

  // Everything else, "/" included: the map is the home page for everyone, not
  // only for visitors. Three counties that publish different things make
  // "which county am I looking at?" the first question, not an afterthought.
  return { kind: "coverage" };
}
